import { useCallback, useState, type Ref } from 'react'
import { View, type NativeSyntheticEvent, type TextInput, type TextInputChangeEventData } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Textarea } from '@/src/elements/input/Textarea'
import type { TCommentObjectType } from '@/src/features/comment/api/types/commentApi.types'
import { useCreateCommentMutation } from '@/src/features/comment/services/useCreateCommentMutation'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { alert } from '@/src/general/utils/alert'
import type { TWithBrowserTextareaHeightProps } from '@/src/general/utils/withBrowserTextareaHeight'
import { useTranslate } from '@/src/lang/useTranslate'

type TCommentTextareaProps = {
  publicationId: number
  communityId: number
  objectType: TCommentObjectType
  onSubmitComment: VoidFunction
  autofocus?: boolean
  ref?: Ref<TextInput>
}

export const CommentTextarea = ({
  ref,
  publicationId,
  communityId,
  objectType,
  autofocus,
  onSubmitComment,
  onLayout,
  onValueUpdate,
}: TCommentTextareaProps & Partial<TWithBrowserTextareaHeightProps>) => {
  const [showSubmitButton, setShowSubmitButton] = useState(false)
  const [content, setContent] = useState<string>('')
  const { t } = useTranslate()

  const updateContent = useCallback(
    (newContent: string) => {
      setContent(newContent)
      onValueUpdate?.()
    },
    [onValueUpdate],
  )

  const onCreateCommentMutation = useCreateCommentMutation(publicationId, {
    content,
    community: communityId,
    objectId: publicationId,
    objectType,
  })

  const handleSubmit = useCallback(() => {
    if (!onCreateCommentMutation.isPending) {
      onCreateCommentMutation
        .mutateAsync()
        .catch((error) => {
          alert('Error', error)
        })
        .then(() => {
          onSubmitComment()
          updateContent('')
        })
    }
  }, [onCreateCommentMutation, onSubmitComment, updateContent])

  const handleValueChange = useCallback(
    (event: NativeSyntheticEvent<TextInputChangeEventData>) => {
      const shouldShowButton = event.nativeEvent.text.trim().length > 0
      updateContent(event.nativeEvent.text)
      if (shouldShowButton !== showSubmitButton) {
        setShowSubmitButton(shouldShowButton)
      }
    },
    [showSubmitButton, setShowSubmitButton, updateContent],
  )

  return (
    <View className="flex flex-row gap-2">
      <Textarea
        className="flex-1"
        ref={ref}
        value={content}
        placeholder={t('comment.write.your-comment')}
        numberOfLines={1}
        onChange={handleValueChange}
        onLayout={onLayout}
        testID={TEST_ID.PUBLICATION.COMMENTS.INPUT}
        autofocus={autofocus}
      />
      {!!showSubmitButton && (
        <Button
          isDisabled={onCreateCommentMutation.isPending}
          testID={TEST_ID.PUBLICATION.COMMENTS.SUBMIT_BUTTON}
          variant="solid"
          colors="primary"
          onPress={handleSubmit}
          isIconOnly
          startIcon={ICON.SEND}
        />
      )}
    </View>
  )
}
